import { useEffect, useState } from "react";
import { api, formatRupiah } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  TrendingUp, TrendingDown, Minus, AlertTriangle, AlertCircle, Info,
  CheckCircle2, Star, Wallet, Banknote, Award, Sparkles,
} from "lucide-react";
import {
  ResponsiveContainer, LineChart, Line, BarChart, Bar, PieChart, Pie, Cell,
  XAxis, YAxis, CartesianGrid, Tooltip, Legend,
} from "recharts";

const COLORS = {
  primary: "#0055FF",
  success: "#16a34a",
  warning: "#f59e0b",
  danger: "#dc2626",
  purple: "#9333ea",
  zinc: "#71717a",
};
const PIE_PALETTE = [
  "#0055FF", "#16a34a", "#f59e0b", "#9333ea", "#dc2626",
  "#0891b2", "#db2777", "#7c3aed", "#65a30d", "#0d9488",
];

const fmtMonth = (ym) => {
  if (!ym) return "—";
  const [y, m] = ym.split("-");
  const bln = ["Jan", "Feb", "Mar", "Apr", "Mei", "Jun", "Jul", "Agt", "Sep", "Okt", "Nov", "Des"];
  return `${bln[parseInt(m) - 1] || m} '${y.slice(2)}`;
};

const humanizeMin = (m) => {
  if (!m) return "0j";
  const h = Math.floor(m / 60);
  const mm = m % 60;
  if (h === 0) return `${mm}m`;
  if (mm === 0) return `${h}j`;
  return `${h}j ${mm}m`;
};

export default function DashboardInsights({ period }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  useEffect(() => {
    let cancel = false;
    setLoading(true);
    setError(false);
    api.get("/dashboard/insights", { params: { period } })
      .then((r) => { if (!cancel) setData(r.data); })
      .catch(() => { if (!cancel) setError(true); })
      .finally(() => { if (!cancel) setLoading(false); });
    return () => { cancel = true; };
  }, [period]);

  if (loading) {
    return (
      <div className="text-center py-8 text-sm text-zinc-500" data-testid="insights-loading">
        <Sparkles className="w-5 h-5 mx-auto mb-2 animate-pulse text-primary" />
        Memuat analitik lanjutan...
      </div>
    );
  }
  if (error || !data) return null;

  return (
    <div className="space-y-8 mt-8" data-testid="dashboard-insights">
      {/* SMART ALERTS */}
      {data.alerts && data.alerts.length > 0 && <AlertsSection alerts={data.alerts} />}

      {/* COMPARISON VS LAST MONTH */}
      <ComparisonSection comparison={data.comparison} />

      {/* 6 MONTH TREND */}
      <TrendSection trend={data.trend_6m} />

      {/* PUNCTUALITY */}
      <PunctualitySection
        arrival={data.arrival_distribution}
        weekday={data.weekday_pattern}
        leaderboard={data.top_punctual}
      />

      {/* FINANCIAL / PAYROLL */}
      <FinancialSection payroll={data.payroll} otWeeks={data.overtime_per_week} />

      {/* LOANS */}
      <LoansSection loans={data.loans} />
    </div>
  );
}

/* ============ SMART ALERTS ============ */
const ALERT_STYLE = {
  warning: { bg: "bg-amber-50", border: "border-amber-300", text: "text-amber-800", iconColor: "text-amber-600", Icon: AlertTriangle },
  danger: { bg: "bg-red-50", border: "border-red-300", text: "text-red-800", iconColor: "text-red-600", Icon: AlertCircle },
  info: { bg: "bg-blue-50", border: "border-blue-300", text: "text-blue-800", iconColor: "text-blue-600", Icon: Info },
  success: { bg: "bg-green-50", border: "border-green-300", text: "text-green-800", iconColor: "text-green-600", Icon: CheckCircle2 },
};

const ICON_OVERRIDE = { star: Star, loan: Banknote, wallet: Wallet, trend: TrendingUp };

const AlertsSection = ({ alerts }) => (
  <div data-testid="alerts-section">
    <SectionTitle title="🚨 Smart Alerts" subtitle={`${alerts.length} insight otomatis hari ini`} />
    <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
      {alerts.map((a, i) => {
        const style = ALERT_STYLE[a.severity] || ALERT_STYLE.info;
        const Icon = ICON_OVERRIDE[a.icon] || style.Icon;
        return (
          <div
            key={i}
            className={`flex items-start gap-3 p-4 rounded-md border ${style.bg} ${style.border}`}
            data-testid={`alert-${i}`}
          >
            <Icon className={`w-5 h-5 mt-0.5 shrink-0 ${style.iconColor}`} />
            <div className="flex-1 min-w-0">
              <p className={`text-sm font-medium ${style.text}`}>{a.title}</p>
              <p className="text-xs text-zinc-600 mt-0.5 leading-relaxed">{a.detail}</p>
            </div>
          </div>
        );
      })}
    </div>
  </div>
);

/* ============ COMPARISON ============ */
const ComparisonSection = ({ comparison }) => {
  const items = [
    { key: "attendance", label: "Kehadiran", unit: " hari" },
    { key: "late_days", label: "Hari Telat", unit: " hari", inverse: true },
    { key: "overtime_min", label: "Jam Lembur", isMin: true },
    { key: "overtime_wage", label: "Uang Lembur", isRp: true },
    { key: "deduction", label: "Potongan Telat", isRp: true, inverse: true },
    { key: "kasbon_amount", label: "Kasbon", isRp: true, inverse: true },
    { key: "payroll_est", label: "Estimasi Payroll", isRp: true },
  ];
  return (
    <div data-testid="comparison-section">
      <SectionTitle
        title="📊 vs Bulan Lalu"
        subtitle="Perbandingan bulan berjalan dengan bulan sebelumnya"
      />
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3">
        {items.map((it) => {
          const c = comparison[it.key];
          if (!c) return null;
          const positive = (c.delta || 0) > 0;
          const negative = (c.delta || 0) < 0;
          const isGood = it.inverse ? negative : positive;
          const isBad = it.inverse ? positive : negative;
          const color = c.delta === 0
            ? "text-zinc-500"
            : isGood ? "text-green-700" : isBad ? "text-red-700" : "text-zinc-500";
          const ArrowIcon = c.delta === 0 ? Minus : positive ? TrendingUp : TrendingDown;
          let curVal;
          if (it.isRp) curVal = formatRupiah(c.current);
          else if (it.isMin) curVal = humanizeMin(c.current);
          else curVal = `${c.current}${it.unit || ""}`;
          let deltaVal;
          if (c.delta === 0) deltaVal = "0";
          else if (it.isRp) deltaVal = formatRupiah(Math.abs(c.delta));
          else if (it.isMin) deltaVal = humanizeMin(Math.abs(c.delta));
          else deltaVal = `${Math.abs(c.delta)}${it.unit || ""}`;
          return (
            <Card key={it.key} className="p-3 border border-zinc-200 shadow-none" data-testid={`cmp-${it.key}`}>
              <p className="overline text-[0.6rem] mb-1 truncate">{it.label}</p>
              <p className="font-display text-base sm:text-lg font-bold leading-tight truncate">{curVal}</p>
              <div className={`text-xs mt-1 flex items-center gap-1 ${color}`}>
                <ArrowIcon className="w-3 h-3 shrink-0" />
                <span className="truncate">
                  {positive ? "+" : negative ? "−" : ""}{deltaVal}
                  {c.pct !== null && c.pct !== undefined && c.delta !== 0 && (
                    <span className="ml-1">({c.pct > 0 ? "+" : ""}{c.pct}%)</span>
                  )}
                </span>
              </div>
            </Card>
          );
        })}
      </div>
    </div>
  );
};

/* ============ TREND 6 BULAN ============ */
const TrendSection = ({ trend }) => {
  const data = trend.map((t) => ({
    ...t,
    label: fmtMonth(t.period),
    overtime_hour: Math.round((t.overtime_min || 0) / 60),
    payroll_juta: Math.round((t.payroll_est || 0) / 1_000_000 * 10) / 10,
  }));
  return (
    <div data-testid="trend-6m-section">
      <SectionTitle title="📈 Tren 6 Bulan Terakhir" subtitle="Performa operasional & finansial" />
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-1">Kehadiran & Keterlambatan</p>
          <h3 className="font-display text-base font-semibold mb-3">Operasional</h3>
          <div style={{ width: "100%", height: 250 }}>
            <ResponsiveContainer>
              <LineChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Line type="monotone" dataKey="attendance" name="Hadir" stroke={COLORS.primary} strokeWidth={2} dot={{ r: 3 }} />
                <Line type="monotone" dataKey="late_days" name="Hari Telat" stroke={COLORS.warning} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-1">Lembur & Payroll</p>
          <h3 className="font-display text-base font-semibold mb-3">Finansial (jam & juta Rp)</h3>
          <div style={{ width: "100%", height: 250 }}>
            <ResponsiveContainer>
              <LineChart data={data} margin={{ top: 5, right: 5, bottom: 0, left: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="left" tick={{ fontSize: 11 }} />
                <YAxis yAxisId="right" orientation="right" tick={{ fontSize: 11 }} />
                <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
                <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
                <Line yAxisId="left" type="monotone" dataKey="overtime_hour" name="Lembur (jam)" stroke={COLORS.purple} strokeWidth={2} dot={{ r: 3 }} />
                <Line yAxisId="right" type="monotone" dataKey="payroll_juta" name="Payroll (jt Rp)" stroke={COLORS.success} strokeWidth={2} dot={{ r: 3 }} />
              </LineChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ============ PUNCTUALITY ============ */
const PunctualitySection = ({ arrival, weekday, leaderboard }) => (
  <div data-testid="punctuality-section">
    <SectionTitle title="⏱ Pola Kedisiplinan" subtitle="Bulan ini" />
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Arrival Distribution */}
      <Card className="p-5 border border-zinc-200 shadow-none">
        <p className="overline mb-1">Distribusi Waktu Datang</p>
        <h3 className="font-display text-sm font-semibold mb-3">Berapa orang datang jam berapa</h3>
        <div style={{ width: "100%", height: 220 }} data-testid="arrival-chart">
          <ResponsiveContainer>
            <BarChart data={arrival} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="bucket" tick={{ fontSize: 10 }} angle={-30} textAnchor="end" height={50} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip contentStyle={{ fontSize: 12, borderRadius: 4 }} />
              <Bar dataKey="count" fill={COLORS.primary} radius={[4, 4, 0, 0]}>
                {arrival.map((entry, i) => {
                  // 08:00-08:10 hijau (tepat waktu), 08:10-08:30 biru (normal),
                  // 08:30+ merah (telat)
                  const isOnTime = entry.bucket === "08:00-08:10";
                  const isLate = entry.bucket.startsWith("08:30") || entry.bucket.startsWith(">09");
                  const color = isLate ? COLORS.danger : isOnTime ? COLORS.success : COLORS.primary;
                  return <Cell key={i} fill={color} />;
                })}
              </Bar>
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Weekday Pattern */}
      <Card className="p-5 border border-zinc-200 shadow-none">
        <p className="overline mb-1">Pola Mingguan</p>
        <h3 className="font-display text-sm font-semibold mb-3">Hari paling sering telat</h3>
        <div style={{ width: "100%", height: 220 }} data-testid="weekday-chart">
          <ResponsiveContainer>
            <BarChart data={weekday} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
              <XAxis dataKey="day" tick={{ fontSize: 11 }} />
              <YAxis allowDecimals={false} tick={{ fontSize: 11 }} />
              <Tooltip
                contentStyle={{ fontSize: 12, borderRadius: 4 }}
                formatter={(v, n, p) => [`${v} hari (${p.payload.late_pct}%)`, n]}
              />
              <Legend iconSize={10} wrapperStyle={{ fontSize: 11 }} />
              <Bar dataKey="present" name="Hadir" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              <Bar dataKey="late" name="Telat" fill={COLORS.warning} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        </div>
      </Card>

      {/* Punctuality Leaderboard */}
      <Card className="p-5 border border-zinc-200 shadow-none" data-testid="punctual-leaderboard">
        <p className="overline mb-1">🏆 Top Tepat Waktu</p>
        <h3 className="font-display text-sm font-semibold mb-3">Datang ≤ 08:10 paling konsisten</h3>
        {leaderboard.length === 0 ? (
          <p className="text-sm text-zinc-500 text-center py-6">Belum ada data cukup</p>
        ) : (
          <ul className="divide-y divide-zinc-100">
            {leaderboard.map((p, i) => (
              <li key={p.user_id} className="flex items-center justify-between py-2">
                <div className="flex items-center gap-3 min-w-0">
                  <span className={`w-7 h-7 rounded-full text-xs font-bold flex items-center justify-center shrink-0 ${
                    i === 0 ? "bg-amber-100 text-amber-700"
                    : i === 1 ? "bg-zinc-200 text-zinc-700"
                    : i === 2 ? "bg-orange-100 text-orange-700"
                    : "bg-zinc-100 text-zinc-600"
                  }`}>
                    {i === 0 ? "🥇" : i === 1 ? "🥈" : i === 2 ? "🥉" : i + 1}
                  </span>
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{p.name}</p>
                    <p className="text-xs text-zinc-500">{p.on_time}/{p.total_days} hari</p>
                  </div>
                </div>
                <div className="text-right shrink-0">
                  <p className="text-sm font-bold text-green-700">{p.punctuality}%</p>
                </div>
              </li>
            ))}
          </ul>
        )}
      </Card>
    </div>
  </div>
);

/* ============ FINANCIAL ============ */
const FinancialSection = ({ payroll, otWeeks }) => {
  const comp = payroll.components;
  const breakdown = [
    { name: "Gaji Pokok", value: comp.gaji_pokok, color: COLORS.primary },
    { name: "Lembur", value: comp.lembur, color: COLORS.success },
    { name: "PHL", value: comp.phl, color: COLORS.purple },
  ].filter((d) => d.value > 0);
  const deductions = [
    { name: "Potongan Telat", value: comp.potongan_telat },
    { name: "Kasbon", value: comp.kasbon },
    { name: "Cicilan Pinjaman", value: comp.loan_repayment },
  ].filter((d) => d.value > 0);
  return (
    <div data-testid="financial-section">
      <SectionTitle title="💰 Finansial / Payroll" subtitle="Estimasi pengeluaran gaji bulan ini" />
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* Big Payroll Estimate Card */}
        <Card className="p-5 border-2 border-primary/30 bg-blue-50/40 shadow-none" data-testid="payroll-estimate">
          <p className="overline mb-1">Estimasi Take Home Total</p>
          <h3 className="font-display text-sm font-semibold">Semua pegawai bulan ini</h3>
          <p className="font-display text-2xl md:text-3xl font-bold tracking-tight text-primary mt-3 break-all">
            {formatRupiah(payroll.estimate)}
          </p>
          <div className="mt-4 space-y-1 text-xs">
            <div className="flex justify-between text-zinc-600">
              <span>+ Gaji Pokok</span>
              <span className="font-mono">{formatRupiah(comp.gaji_pokok)}</span>
            </div>
            <div className="flex justify-between text-green-700">
              <span>+ Lembur</span>
              <span className="font-mono">{formatRupiah(comp.lembur)}</span>
            </div>
            <div className="flex justify-between text-green-700">
              <span>+ PHL</span>
              <span className="font-mono">{formatRupiah(comp.phl)}</span>
            </div>
            <div className="flex justify-between text-red-700">
              <span>− Potongan Telat</span>
              <span className="font-mono">({formatRupiah(comp.potongan_telat)})</span>
            </div>
            <div className="flex justify-between text-red-700">
              <span>− Kasbon</span>
              <span className="font-mono">({formatRupiah(comp.kasbon)})</span>
            </div>
            <div className="flex justify-between text-red-700">
              <span>− Cicilan Pinjaman</span>
              <span className="font-mono">({formatRupiah(comp.loan_repayment)})</span>
            </div>
          </div>
        </Card>

        {/* Breakdown Pie */}
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-1">Komposisi Penghasilan</p>
          <h3 className="font-display text-sm font-semibold mb-2">Gross income breakdown</h3>
          <div style={{ width: "100%", height: 220 }} data-testid="income-pie">
            <ResponsiveContainer>
              <PieChart>
                <Pie data={breakdown} dataKey="value" outerRadius={80} label={(e) => `${e.name}`} labelLine={false}>
                  {breakdown.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                </Pie>
                <Tooltip formatter={(v) => formatRupiah(v)} />
              </PieChart>
            </ResponsiveContainer>
          </div>
          {deductions.length > 0 && (
            <div className="mt-2 pt-2 border-t border-zinc-200">
              <p className="text-xs text-zinc-500 mb-1">Total potongan:</p>
              <p className="font-mono text-sm font-bold text-red-700">
                {formatRupiah(deductions.reduce((s, d) => s + d.value, 0))}
              </p>
            </div>
          )}
        </Card>

        {/* Lembur per Week */}
        <Card className="p-5 border border-zinc-200 shadow-none" data-testid="ot-weeks">
          <p className="overline mb-1">Cashflow Lembur Mingguan</p>
          <h3 className="font-display text-sm font-semibold mb-3">4 minggu terakhir (Min–Sab)</h3>
          <div style={{ width: "100%", height: 220 }}>
            <ResponsiveContainer>
              <BarChart data={otWeeks} margin={{ top: 5, right: 5, bottom: 0, left: -10 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e4e4e7" />
                <XAxis dataKey="label" tick={{ fontSize: 10 }} />
                <YAxis
                  tick={{ fontSize: 10 }}
                  tickFormatter={(v) => `${(v / 1_000_000).toFixed(1)}jt`}
                />
                <Tooltip
                  contentStyle={{ fontSize: 12, borderRadius: 4 }}
                  formatter={(v) => formatRupiah(v)}
                />
                <Bar dataKey="overtime_wage" name="Uang Lembur" fill={COLORS.success} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        </Card>
      </div>
    </div>
  );
};

/* ============ LOANS SECTION ============ */
const LoansSection = ({ loans }) => {
  if (!loans) return null;
  const catData = loans.by_category.map((c, i) => ({
    name: c.category,
    value: c.outstanding,
    count: c.count,
    color: PIE_PALETTE[i % PIE_PALETTE.length],
  }));
  return (
    <div data-testid="loans-insights-section">
      <SectionTitle title="💳 Pinjaman Pegawai" subtitle="Status keseluruhan program pinjaman" />

      {/* KPIs */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-4">
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Outstanding Total</p>
          <p className="font-display text-base md:text-lg font-bold text-amber-700 truncate" data-testid="loans-kpi-outstanding">
            {formatRupiah(loans.total_outstanding)}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Cicilan Bulan Ini</p>
          <p className="font-display text-base md:text-lg font-bold text-zinc-800 truncate" data-testid="loans-kpi-monthly">
            {formatRupiah(loans.monthly_deduction)}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Pinjaman Aktif</p>
          <p className="font-display text-xl font-bold tracking-tight" data-testid="loans-kpi-active">
            {loans.active_count}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Lunas Bulan Ini</p>
          <p className="font-display text-xl font-bold tracking-tight text-green-700" data-testid="loans-kpi-paid">
            {loans.paid_off_this_month}
          </p>
        </Card>
        <Card className="p-4 border border-zinc-200 shadow-none">
          <p className="overline text-[0.6rem] mb-1">Baru Bulan Ini</p>
          <p className="font-display text-xl font-bold tracking-tight text-primary" data-testid="loans-kpi-new">
            {loans.new_this_month}
          </p>
        </Card>
      </div>

      {/* Pie + Tops */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        {/* By Category */}
        <Card className="p-5 border border-zinc-200 shadow-none">
          <p className="overline mb-1">Distribusi per Kategori</p>
          <h3 className="font-display text-sm font-semibold mb-2">Outstanding per kategori</h3>
          {catData.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-8">Belum ada pinjaman aktif</p>
          ) : (
            <div style={{ width: "100%", height: 220 }} data-testid="loans-pie">
              <ResponsiveContainer>
                <PieChart>
                  <Pie data={catData} dataKey="value" outerRadius={80} label={(e) => `${e.name}`} labelLine={false}>
                    {catData.map((entry, i) => <Cell key={i} fill={entry.color} />)}
                  </Pie>
                  <Tooltip formatter={(v, n, p) => [`${formatRupiah(v)} (${p.payload.count} pinjaman)`, n]} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Top Borrowers */}
        <Card className="p-5 border border-zinc-200 shadow-none" data-testid="top-borrowers">
          <p className="overline mb-1">Top Outstanding</p>
          <h3 className="font-display text-sm font-semibold mb-3">Pinjaman terbesar saat ini</h3>
          {loans.top_borrowers.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-6">Belum ada</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {loans.top_borrowers.map((b, i) => (
                <li key={b.loan_id} className="py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{b.user_name}</p>
                      <p className="text-xs text-zinc-500 truncate">
                        {b.title} · <Badge variant="outline" className="text-[0.6rem] ml-0.5">{b.category}</Badge>
                      </p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-amber-700">{formatRupiah(b.outstanding)}</p>
                      <p className="text-[0.6rem] text-zinc-400">/{formatRupiah(b.principal)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>

        {/* Nearly Paid Off */}
        <Card className="p-5 border border-zinc-200 shadow-none" data-testid="nearly-paid-off">
          <p className="overline mb-1">🎯 Hampir Lunas</p>
          <h3 className="font-display text-sm font-semibold mb-3">Sisa ≤ 2x cicilan</h3>
          {loans.nearly_paid_off.length === 0 ? (
            <p className="text-sm text-zinc-500 text-center py-6">Belum ada yang mendekati lunas</p>
          ) : (
            <ul className="divide-y divide-zinc-100">
              {loans.nearly_paid_off.map((b) => (
                <li key={b.loan_id} className="py-2">
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <p className="text-sm font-medium truncate">{b.user_name}</p>
                      <p className="text-xs text-zinc-500 truncate">{b.title}</p>
                    </div>
                    <div className="text-right shrink-0">
                      <p className="text-sm font-bold text-green-700">{formatRupiah(b.outstanding)}</p>
                      <p className="text-[0.6rem] text-zinc-400">cicilan {formatRupiah(b.monthly_minimum)}</p>
                    </div>
                  </div>
                </li>
              ))}
            </ul>
          )}
        </Card>
      </div>
    </div>
  );
};

const SectionTitle = ({ title, subtitle }) => (
  <div className="mb-3">
    <h2 className="font-display text-base md:text-lg font-semibold tracking-tight">{title}</h2>
    {subtitle && <p className="text-xs text-zinc-500 mt-0.5">{subtitle}</p>}
  </div>
);
